import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const TRANSITION_MS = 260;

/** Shared slide-up/fade sheet chrome for the mobile nav quick-panels (Tourneys, Squads,
 *  Events, Live). Stays mounted through the close animation so switching directly from one
 *  panel to another crossfades instead of hard-cutting. */
const BottomSheet = ({ isOpen, onClose, children }) => {
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(isOpen);
  const [entered, setEntered] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    let raf;
    let timer;
    if (isOpen) {
      setMounted(true);
    } else {
      setEntered(false);
      timer = setTimeout(() => setMounted(false), TRANSITION_MS);
    }
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [isOpen]);

  // Runs after `mounted` commits the closed (translate-y-full) position. rAF alone isn't
  // reliable here — on heavy pages (e.g. the Tournaments grid) the browser can coalesce
  // the "closed" and "open" frames under paint pressure, so the sheet just snaps instead of
  // sliding. Forcing a synchronous reflow (offsetHeight) makes the browser commit the closed
  // layout immediately, before we flip to open on the next frame.
  useEffect(() => {
    if (!isOpen || !mounted) return;
    if (sheetRef.current) void sheetRef.current.offsetHeight;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen, mounted]);

  if (!mounted) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 md:hidden transition-opacity duration-200 ease-out ${entered ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        className={`fixed left-0 right-0 z-50 md:hidden ${theme.card} rounded-t-2xl shadow-2xl flex flex-col transition-transform duration-300 ease-out ${entered ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
      >
        {children}
      </div>
    </>
  );
};

export default BottomSheet;
